/**
 * TemplateErrorBoundary — isolates AWAC template crashes from the chat.
 *
 * If a template throws during render (malformed envelope payload, template
 * bug, lazy-load failure), the conversation must keep working: the boundary
 * swallows the error, shows a small bilingual fallback inside the gallery
 * wing, and offers a retry that re-mounts the template subtree.
 */

import React from 'react';
import { t } from './i18n';

export default class TemplateErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, attempt: 0 };
        this.handleRetry = this.handleRetry.bind(this);
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error) {
        // Surface for debugging without crashing the app.
        // eslint-disable-next-line no-console
        console.error('[AWAC] Template render failed:', error);
    }

    componentDidUpdate(prevProps) {
        // A new envelope (new resetKey) gets a fresh chance to render.
        if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ hasError: false });
        }
    }

    handleRetry() {
        this.setState((prev) => ({ hasError: false, attempt: prev.attempt + 1 }));
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="awacTemplateError" role="alert">
                    <p className="awacTemplateErrorTitle">{t('tpl.errorTitle')}</p>
                    <p className="awacTemplateErrorBody">{t('tpl.errorBody')}</p>
                    <button
                        type="button"
                        className="awacTemplateErrorRetry"
                        onClick={this.handleRetry}
                    >
                        {t('tpl.retry')}
                    </button>
                </div>
            );
        }

        // attempt is part of the key upstream so retry re-mounts children.
        return (
            <React.Fragment key={this.state.attempt}>
                {this.props.children}
            </React.Fragment>
        );
    }
}
